import React from 'react';

import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  Heading16,
  LoadingButton,
  ModalDialog,
} from '@aboutyou/ui-components';
import styled from 'styled-components';
import {useDeleteWishlistPageItem} from '../hooks/useWishlistPageStream';

function handlePreventClickEvent(event?: React.MouseEvent) {
  event?.stopPropagation();
  event?.preventDefault();
}

interface RemoveItemConfirmModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
  onClickConfirm: VoidFunction;
  isLoading: boolean;
  onClickRemoveItemSuccess: VoidFunction;
  onClickRemoveItemError: VoidFunction;
  productId: number;
}

export const RemoveItemConfirmModal = ({
  isOpen,
  onClose,
  onClickConfirm,
  isLoading,
  onClickRemoveItemError,
  onClickRemoveItemSuccess,
  productId,
}: RemoveItemConfirmModalProps) => {
  const {t} = useTranslation();

  // this hook is expensive, only scoping it to the remove item button will reduce expensive re-render due to changes on the wishlist
  const onDelete = useDeleteWishlistPageItem();
  const handleClickButton = async () => {
    onClickConfirm();
    const success = await onDelete(productId);
    if (success) {
      onClickRemoveItemSuccess();
    } else {
      onClickRemoveItemError();
    }
  };

  return (
    <ModalDialog
      showCloseIcon={false}
      renderInPortal
      showHeaderDivider
      bodyContent={
        <ModalText>
          {t('Do you really want to remove this product from your wishlist?')}
        </ModalText>
      }
      footerContent={
        <>
          <CloseButton
            onClick={e => {
              handlePreventClickEvent(e);
              onClose();
            }}
            disableShadow
            withBorder
            type="secondary"
          >
            {t('Cancel')}
          </CloseButton>
          <StyledLoadingButton
            data-testid="WishlistConfirmDeletionButton"
            isLoading={isLoading}
            disableShadow
            onClick={e => {
              handlePreventClickEvent(e);
              handleClickButton();
            }}
          >
            {t('Remove')}
          </StyledLoadingButton>
        </>
      }
      onClose={onClose}
      open={isOpen}
    />
  );
};

const ModalText = styled(Heading16)`
  padding: 20px;
  text-align: center;
`;

const CloseButton = styled(Button)`
  padding-inline: 0px;
`;

const StyledLoadingButton = styled(LoadingButton)`
  padding-inline: 0px;
`;
